export type PlanV2IssueType =
  | 'primary_action'
  | 'topic_ownership'
  | 'freshness'
  | 'serp_competition'
  | 'serp_phrase'
  | 'serp_difficulty'
  | 'serp_intent'
  | 'technical_critical'
  | 'technical_warnings'
  | 'signals'
  | 'internal_link'
  | 'url_ignored'
  /** One template defect, decided once for the period. See the backend model. */
  | 'site_wide_technical'
  /**
   * Sections the top-ranking rival covers that this page does not.
   *
   * Split out of `topic_ownership`, which was answering two questions with one
   * tick: pages to WRITE and sections to ADD are different work, land in
   * different places, and an operator wanting one and not the other had no way
   * to say so. Review surface only for now — see `docs/review-surface-debt.md`.
   */
  | 'section_gap'
  /**
   * A check the operator unticked, remembered for the rest of the period.
   *
   * Not a finding — a preference about a finding. Keyed by CODE, so the next
   * page arrives with the same check already unticked and one click away from
   * being ticked again. See `utils/review-check-memory`.
   */
  | 'check_muted';

export type PlanV2IssueQueue = 'issues' | 'work_item' | 'dismissed';

export interface PlanV2IssueCardPayload {
  issueKey: string;
  urlId: string;
  url: string;
  issueType: PlanV2IssueType;
  cardTitle: string;
  changeSummary: string;
  why: string;
  /** Actionable checklist / examples for Work items (multi-line). */
  actionDetail?: string | null;
  /** Internal-link rows: stable id from action plan (`suggestionKey`). */
  suggestionKey?: string | null;
  diagnosisLabel?: string | null;
  priorityScore?: number | null;
}

/** Mirrors `backend/src/utils/plan-v2-brief-period.ts`. */
export type PlanV2IssuePeriodBucket = 'current' | 'previous';

export type PlanV2BriefPeriodState = 'absent' | 'first-run' | 'compared';

export interface PlanV2BriefPeriod {
  state: PlanV2BriefPeriodState;
  currentRunId: string | null;
  previousRunId: string | null;
  currentRunCompletedAt: string | null;
  previousRunCompletedAt: string | null;
  currentPeriodStartedAt: string | null;
  hasPrevious: boolean;
}

/** Which triage a regenerate is allowed to clear. */
export type PlanV2RegenerateScope = 'current' | 'previous' | 'all';

/**
 * Automatic history state (LB4) — see `backend/src/utils/living-brief-history-state.ts`.
 * A compare label only: it never closes, hides or reorders anything.
 */
export type PlanV2HistoryState =
  | 'still_open'
  | 'looks_resolved'
  | 'no_longer_relevant'
  | 'too_early_to_tell';

export interface PlanV2IssueDispositionRecord extends PlanV2IssueCardPayload {
  queue: 'work_item' | 'dismissed';
  /** Absent on records composed client-side (compose previews) — those are current. */
  periodBucket?: PlanV2IssuePeriodBucket;
  updatedAt: string;
  /** LB3 period binding. Absent on responses from a pre-LB3 backend. */
  periodCrawlRunId?: string | null;
  /** Non-null = frozen into Previous by a chapter turn; compare only. */
  archivedAt?: string | null;
  /** Set on a Previous row the operator re-committed to a later period. */
  carriedForwardAt?: string | null;
  carriedForwardFromCrawlRunId?: string | null;
  /** Null on `url_ignored` rows, which are an inventory decision, not advice. */
  historyState?: PlanV2HistoryState | null;
  /** The backend's operator copy for the state. */
  historyStateLabel?: string | null;
  /** The evidence behind the state, in one sentence. */
  historyStateReason?: string | null;
  /** Which rule fired, for support and for the pilot smoke. */
  historyStateRule?: string | null;
}

/** The crawl-run pair the living brief covers (LIVING_BRIEF_PLAN §1). Null when
 *  the domain has no captured run — then there is no Previous, and no dates. */
export interface PlanV2LivingBriefPeriod {
  crawlRunId: string;
  previousCrawlRunId: string | null;
  completedAt: string | null;
  previousCompletedAt: string | null;
}

export function emptyPlanV2BriefPeriod(): PlanV2BriefPeriod {
  return {
    state: 'absent',
    currentRunId: null,
    previousRunId: null,
    currentRunCompletedAt: null,
    previousRunCompletedAt: null,
    currentPeriodStartedAt: null,
    hasPrevious: false
  };
}

/**
 * A record with no bucket belongs to the living brief. The server stamps every
 * persisted row; the fallback covers older responses and client-side previews,
 * and erring towards "current" keeps an actionable item where it can be acted on.
 */
export function planV2RecordPeriodBucket(
  record: Pick<PlanV2IssueDispositionRecord, 'periodBucket'>
): PlanV2IssuePeriodBucket {
  return record?.periodBucket === 'previous' ? 'previous' : 'current';
}

export function isPlanV2CurrentPeriodRecord(
  record: Pick<PlanV2IssueDispositionRecord, 'periodBucket'>
): boolean {
  return planV2RecordPeriodBucket(record) === 'current';
}

/** Work items grouped for CMS-style execution (one page → many tasks). */
export interface PlanV2WorkItemsUrlGroup {
  urlId: string;
  url: string;
  priorityScore: number;
  items: PlanV2IssueDispositionRecord[];
}

export function buildPlanV2IssueKey(
  urlId: string,
  issueType: PlanV2IssueType,
  suffix?: string | null
): string {
  const base = `${urlId.trim()}:${issueType}`;
  const extra = (suffix || '').trim();
  return extra ? `${base}:${extra}` : base;
}

export function resolvePlanV2IssueQueue(
  issueKey: string,
  records: Map<string, PlanV2IssueDispositionRecord>
): PlanV2IssueQueue {
  const hit = records.get(issueKey);
  if (!hit) return 'issues';
  return hit.queue;
}

export function isPlanV2IssueVisibleInEmbed(
  issueKey: string,
  records: Map<string, PlanV2IssueDispositionRecord>
): boolean {
  return resolvePlanV2IssueQueue(issueKey, records) === 'issues';
}
